import { useState } from "react";
import ReactSelect from "react-select";
import FormActions from "../../../../components/Form/FormActions";
import { COLORS } from "../../../../constants";
import Switch from "../../../../components/Switch";
import { Meta } from "../../../../types/meta";
import { metaCreateSchema } from "../../../../lib/validations";
import { z } from "zod";
import { BrazilDate } from "../../../../utils/BrazilDate";

interface MetaFormProps {
  initialData?: Meta;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  lojas: Array<{ id: number; nome: string }>;
  usuarios: Array<{ id: number; nome: string }>;
}

const MetaForm = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
  lojas,
  usuarios,
}: MetaFormProps) => {
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return BrazilDate.formatISO(date);
  };

  const [formData, setFormData] = useState({
    tipo: initialData?.tipo || "",
    tempo: initialData?.tempo || "",
    descricao: initialData?.descricao || "",
    dataInicio: formatDateForInput(initialData?.dataInicio || ""),
    dataFim: formatDateForInput(initialData?.dataFim || ""),
    ativo: initialData?.ativo ?? true,
    lojaId: initialData?.lojaId || "",
    usuarioIds:
      initialData?.usuarios?.map((u) => u.id) || initialData?.usuarioIds || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const applyMask = (value: string, mask: string) => {
    let maskedValue = "";
    let valueIndex = 0;

    for (let i = 0; i < mask.length && valueIndex < value.length; i++) {
      if (mask[i] === "9") {
        if (/\d/.test(value[valueIndex])) {
          maskedValue += value[valueIndex];
          valueIndex++;
        } else {
          break;
        }
      } else {
        maskedValue += mask[i];
      }
    }

    return maskedValue;
  };

  const handleChange = (name: string, value: any) => {
    if (name === "tempo") {
      const rawValue = value.replace(/\D/g, "");
      const maskedValue = applyMask(rawValue, "99:99:99");
      setFormData((prev) => ({ ...prev, [name]: maskedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const validatedData = metaCreateSchema.parse({
        ...formData,
        lojaId: parseInt(formData.lojaId.toString()) || 0,
        usuarioIds: formData.usuarioIds.map((id: number) =>
          parseInt(id.toString())
        ),
      });

      setErrors({});
      await onSubmit(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.issues.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
      }
    }
  };

  const tipoOptions = [
    { value: "producao", label: "Produção" },
    { value: "entrega", label: "Entrega" },
  ];

  const lojaOptions = lojas.map((loja) => ({
    value: loja.id.toString(),
    label: loja.nome,
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Switch
        label="Ativo"
        checked={formData.ativo}
        onChange={(checked) => handleChange("ativo", checked)}
      />

      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700">
          Usuários
        </label>
        <ReactSelect
          isMulti
          options={usuarios.map((usuario) => ({
            value: usuario.id.toString(),
            label: usuario.nome,
          }))}
          value={usuarios
            .filter((usuario) => formData.usuarioIds.includes(usuario.id))
            .map((usuario) => ({
              value: usuario.id.toString(),
              label: usuario.nome,
            }))}
          onChange={(options) =>
            handleChange(
              "usuarioIds",
              options ? options.map((option) => parseInt(option.value)) : []
            )
          }
          placeholder="Selecione os usuários"
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.usuarioIds
                ? COLORS.ERROR
                : COLORS.NEUTRAL_300,
              "&:hover": {
                borderColor: errors.usuarioIds ? COLORS.ERROR : COLORS.PRIMARY,
              },
              "&:focus-within": {
                borderColor: errors.usuarioIds ? COLORS.ERROR : COLORS.PRIMARY,
                boxShadow: `0 0 0 2px ${errors.usuarioIds ? "rgba(239, 68, 68, 0.2)" : "rgba(147, 32, 24, 0.2)"}`,
              },
            }),
          }}
        />
        {errors.usuarioIds && (
          <p className="mt-1 text-sm text-red-500">{errors.usuarioIds}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Loja
          </label>
          <ReactSelect
            options={lojaOptions}
            value={
              lojaOptions.find(
                (option) => option.value === formData.lojaId.toString()
              ) || null
            }
            onChange={(option) => handleChange("lojaId", option?.value ?? "")}
            placeholder="Selecione uma loja"
            isClearable={false}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.lojaId ? COLORS.ERROR : COLORS.NEUTRAL_300,
                "&:hover": {
                  borderColor: errors.lojaId ? COLORS.ERROR : COLORS.PRIMARY,
                },
                "&:focus-within": {
                  borderColor: errors.lojaId ? COLORS.ERROR : COLORS.PRIMARY,
                  boxShadow: `0 0 0 2px ${errors.lojaId ? "rgba(239, 68, 68, 0.2)" : "rgba(147, 32, 24, 0.2)"}`,
                },
              }),
            }}
          />
          {errors.lojaId && (
            <p className="mt-1 text-sm text-error">{errors.lojaId}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Tipo de Meta
          </label>
          <ReactSelect
            options={tipoOptions}
            value={
              tipoOptions.find((option) => option.value === formData.tipo) ||
              null
            }
            onChange={(option) => handleChange("tipo", option?.value || "")}
            placeholder="Selecione um tipo"
            isClearable
            styles={{
              control: (base) => ({
                ...base,
                borderColor: errors.tipo ? COLORS.ERROR : COLORS.NEUTRAL_300,
                "&:hover": {
                  borderColor: errors.tipo ? COLORS.ERROR : COLORS.PRIMARY,
                },
                "&:focus-within": {
                  borderColor: errors.tipo ? COLORS.ERROR : COLORS.PRIMARY,
                  boxShadow: `0 0 0 2px ${errors.tipo ? "rgba(239, 68, 68, 0.2)" : "rgba(147, 32, 24, 0.2)"}`,
                },
              }),
            }}
          />
          {errors.tipo && (
            <p className="mt-1 text-sm text-error">{errors.tipo}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Tempo
          </label>
          <input
            type="text"
            value={formData.tempo}
            onChange={(e) => handleChange("tempo", e.target.value)}
            placeholder="00:15:00"
            maxLength={8}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.tempo
                ? "border-error focus:ring-error"
                : "border-neutral-300 focus:ring-primary"
            }`}
            required
          />
          {errors.tempo && (
            <p className="mt-1 text-sm text-error">{errors.tempo}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Descrição
          </label>
          <input
            type="text"
            value={formData.descricao}
            onChange={(e) => handleChange("descricao", e.target.value)}
            placeholder="Descrição da meta (máx. 100 caracteres)"
            maxLength={100}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.descricao
                ? "border-error focus:ring-error"
                : "border-neutral-300 focus:ring-primary"
            }`}
            required
          />
          {errors.descricao && (
            <p className="mt-1 text-sm text-error">{errors.descricao}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Data de Início
          </label>
          <input
            type="date"
            value={formData.dataInicio}
            onChange={(e) => handleChange("dataInicio", e.target.value)}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.dataInicio
                ? "border-error focus:ring-error"
                : "border-neutral-300 focus:ring-primary"
            }`}
            required
          />
          {errors.dataInicio && (
            <p className="mt-1 text-sm text-error">{errors.dataInicio}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-neutral-700">
            Data de Fim
          </label>
          <input
            type="date"
            value={formData.dataFim}
            onChange={(e) => handleChange("dataFim", e.target.value)}
            className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 ${
              errors.dataFim
                ? "border-error focus:ring-error"
                : "border-neutral-300 focus:ring-primary"
            }`}
            required
          />
          {errors.dataFim && (
            <p className="mt-1 text-sm text-error">{errors.dataFim}</p>
          )}
        </div>
      </div>

      <FormActions
        onCancel={onCancel}
        isLoading={isLoading}
        submitText={initialData ? "Atualizar Meta" : "Criar Meta"}
      />
    </form>
  );
};

export default MetaForm;
